/**
 * Quote Modal Component
 * Modal for creating and editing quotes with line items
 */

import React, { useState, useEffect, useMemo } from "react";
import { Icon } from "@/components/ui";
import { AlertModal } from "@/components/ui/AlertModal/AlertModal";
import { formatCurrency } from "@/utils/formatters";
import styles from "./QuoteModal.module.css";

interface QuoteLineItem {
  id: string;
  serviceId: string;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  total: number;
}

interface Quote {
  id?: string;
  clientId: string;
  clientName: string;
  date: string;
  validUntil: string;
  serviceStartDate?: string;
  serviceEndDate?: string;
  notes: string;
  lineItems: QuoteLineItem[];
  subtotal: number;
  tax: number;
  total: number;
  pestType?: string;
}

interface QuoteModalProps {
  isOpen: boolean;
  quote: Quote | null;
  initialQuote?: Quote | null;
  clients: Array<{ id: string; name: string }>;
  clientsLoading?: boolean;
  hasMoreClients?: boolean;
  onLoadMoreClients?: () => void;
  plagues: Array<{ id: string; name: string; isRecurrent?: boolean }>;
  /** Servicios del catálogo API. Si se proveen, sustituyen al catálogo local. */
  services?: Array<{ id: string; name: string; price: number }>;
  onClose: () => void;
  onSave: (quote: Quote) => void;
}

const defaultLineItem: QuoteLineItem = {
  id: "",
  serviceId: "",
  description: "",
  quantity: 1,
  unitPrice: 0,
  discount: 0,
  total: 0,
};

const normalizeLineItems = (items?: QuoteLineItem[]): QuoteLineItem[] => {
  if (!items || items.length === 0) {
    return [{ ...defaultLineItem, id: "1" }];
  }

  return items.map((item, index) => ({
    ...defaultLineItem,
    ...item,
    id: item.id || String(index + 1),
    serviceId: item.serviceId ?? "",
    unitPrice:
      typeof item.unitPrice === "number"
        ? item.unitPrice
        : Number.parseFloat(String(item.unitPrice ?? 0)) || 0,
  }));
};

/**
 * QuoteModal component
 */
export function QuoteModal({
  isOpen,
  quote,
  initialQuote = null,
  clients,
  clientsLoading = false,
  hasMoreClients = false,
  onLoadMoreClients,
  plagues,
  services: servicesProp,
  onClose,
  onSave,
}: QuoteModalProps) {
  const defaultDates = useMemo(() => {
    const now = new Date();
    const future = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    return {
      date: now.toISOString().split("T")[0],
      validUntil: future.toISOString().split("T")[0],
    };
  }, []);

  const getInitialData = (): Quote => {
    const sourceQuote = quote ?? initialQuote;

    if (sourceQuote) {
      return {
        ...sourceQuote,
        date: sourceQuote.date || defaultDates.date,
        validUntil: sourceQuote.validUntil || defaultDates.validUntil,
        serviceStartDate: sourceQuote.serviceStartDate || "",
        serviceEndDate: sourceQuote.serviceEndDate || "",
        lineItems: normalizeLineItems(sourceQuote.lineItems),
        notes: sourceQuote.notes || "",
      };
    }
    return {
      clientId: "",
      clientName: "",
      date: defaultDates.date,
      validUntil: defaultDates.validUntil,
      serviceStartDate: "",
      serviceEndDate: "",
      notes: "",
      lineItems: normalizeLineItems(),
      subtotal: 0,
      tax: 0,
      total: 0,
      pestType: undefined,
    };
  };

  const [formData, setFormData] = useState<Quote>(getInitialData);
  const [clientFilter, setClientFilter] = useState("");
  const [serviceCatalog, setServiceCatalog] = useState<
    Array<{ id: string; name: string; price: number }>
  >([]);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "error" | "success";
  }>({
    isOpen: false,
    message: "",
    type: "info",
  });

  useEffect(() => {
    if (isOpen) {
      setFormData(getInitialData());
      setClientFilter("");
      setServiceCatalog(servicesProp ?? []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, servicesProp, quote, initialQuote]);

  const calculateLineTotal = (item: QuoteLineItem): number => {
    const subtotal = item.quantity * item.unitPrice;
    const discountAmount = (subtotal * item.discount) / 100;
    return subtotal - discountAmount;
  };

  const calculateTotals = (items: QuoteLineItem[]) => {
    const subtotal = items.reduce(
      (sum, item) => sum + calculateLineTotal(item),
      0,
    );
    const tax = subtotal * 0.21; // IVA 21%
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const updateLineItem = (
    index: number,
    field: keyof QuoteLineItem,
    value: string | number,
  ) => {
    const newLineItems = [...formData.lineItems];
    newLineItems[index] = {
      ...newLineItems[index],
      [field]: value,
    };
    newLineItems[index].total = calculateLineTotal(newLineItems[index]);

    const totals = calculateTotals(newLineItems);
    setFormData({
      ...formData,
      lineItems: newLineItems,
      ...totals,
    });
  };

  const handleServiceSelection = (index: number, selectedId: string) => {
    const normalizedId = selectedId ?? "";
    const catalogService = serviceCatalog.find(
      (service) => service.id === normalizedId,
    );
    const newLineItems = [...formData.lineItems];
    const updatedLine: QuoteLineItem = {
      ...newLineItems[index],
      serviceId: normalizedId,
      description: catalogService?.name ?? "",
      unitPrice: catalogService?.price ?? 0,
    };

    updatedLine.total = calculateLineTotal(updatedLine);
    newLineItems[index] = updatedLine;

    const totals = calculateTotals(newLineItems);
    setFormData({
      ...formData,
      lineItems: newLineItems,
      ...totals,
    });
  };

  const addLineItem = () => {
    const newLineItems = [
      ...formData.lineItems,
      { ...defaultLineItem, id: String(Date.now()) },
    ];
    setFormData({
      ...formData,
      lineItems: newLineItems,
    });
  };

  const removeLineItem = (index: number) => {
    if (formData.lineItems.length === 1) return;
    const newLineItems = formData.lineItems.filter((_, i) => i !== index);
    const totals = calculateTotals(newLineItems);
    setFormData({
      ...formData,
      lineItems: newLineItems,
      ...totals,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validar que la fecha de validez sea posterior a la fecha de emisión
    if (new Date(formData.validUntil) <= new Date(formData.date)) {
      setAlertModal({
        isOpen: true,
        message: "La fecha de validez debe ser posterior a la fecha de emisión",
        type: "warning",
      });
      return;
    }

    if (
      formData.serviceEndDate &&
      new Date(formData.serviceEndDate) < new Date(formData.date)
    ) {
      setAlertModal({
        isOpen: true,
        message:
          "La fecha fin de servicio no puede ser anterior a la fecha de emisión",
        type: "warning",
      });
      return;
    }

    const selectedPlague = plagues.find((p) => p.id === formData.pestType);
    if (selectedPlague?.isRecurrent) {
      if (!formData.serviceStartDate) {
        setAlertModal({
          isOpen: true,
          message:
            "La fecha de inicio del servicio es obligatoria para plagas recurrentes",
          type: "warning",
        });
        return;
      }
      if (!formData.serviceEndDate) {
        setAlertModal({
          isOpen: true,
          message:
            "La fecha fin de servicio es obligatoria para plagas recurrentes",
          type: "warning",
        });
        return;
      }
      if (new Date(formData.serviceStartDate) < new Date(formData.date)) {
        setAlertModal({
          isOpen: true,
          message:
            "La fecha de inicio del servicio no puede ser anterior a la fecha de emisión",
          type: "warning",
        });
        return;
      }
      if (
        new Date(formData.serviceEndDate) <=
        new Date(formData.serviceStartDate)
      ) {
        setAlertModal({
          isOpen: true,
          message:
            "La fecha fin de servicio debe ser posterior a la fecha de inicio del servicio",
          type: "warning",
        });
        return;
      }
    }

    // Validar que todas las líneas tengan un servicio asignado
    if (formData.lineItems.some((item) => !item.serviceId)) {
      setAlertModal({
        isOpen: true,
        message: "Cada línea de servicio debe tener un servicio seleccionado",
        type: "warning",
      });
      return;
    }

    // Validar que todas las líneas tengan cantidad válida
    if (formData.lineItems.some((item) => item.quantity <= 0)) {
      setAlertModal({
        isOpen: true,
        message: "La cantidad debe ser mayor a 0 en todas las líneas",
        type: "warning",
      });
      return;
    }

    // Validar que todas las líneas tengan precio válido
    if (formData.lineItems.some((item) => item.unitPrice < 0)) {
      if (!formData.clientId) {
        setAlertModal({
          isOpen: true,
          message: "Debes seleccionar un cliente",
          type: "warning",
        });
        return;
      }

      setAlertModal({
        isOpen: true,
        message: "El precio unitario no puede ser negativo",
        type: "error",
      });
      return;
    }

    // Validar que haya al menos una línea de servicio
    if (formData.lineItems.length === 0) {
      setAlertModal({
        isOpen: true,
        message: "Debe agregar al menos una línea de servicio",
        type: "warning",
      });
      return;
    }

    const selectedClient = clients.find((c) => c.id === formData.clientId);
    onSave({
      ...formData,
      clientName: selectedClient?.name || "",
    });
  };

  const handleClientChange = (clientId: string) => {
    const selectedClient = clients.find((c) => c.id === clientId);
    setFormData({
      ...formData,
      clientId,
      clientName: selectedClient?.name || "",
    });
  };

  const filteredClients = useMemo(() => {
    const query = clientFilter.trim().toLowerCase();
    if (!query) return clients;
    return clients.filter((client) =>
      client.name.toLowerCase().includes(query),
    );
  }, [clients, clientFilter]);

  const handleClientListScroll = (
    event: React.UIEvent<HTMLDivElement>,
  ): void => {
    if (!onLoadMoreClients || clientsLoading || !hasMoreClients) return;
    if (clientFilter.trim() !== "") return;

    const target = event.currentTarget;
    const isNearBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

    if (isNearBottom) {
      onLoadMoreClients();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{quote ? "Editar Presupuesto" : "Nuevo Presupuesto"}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <Icon name="xCircle" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Header Info */}
          <div className={styles.headerSection}>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="clientFilter">Cliente *</label>
                <input
                  id="clientFilter"
                  name="clientFilter"
                  type="text"
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                  placeholder="Filtrar cliente por nombre..."
                />
                <div
                  className={styles.clientOptionsList}
                  onScroll={handleClientListScroll}
                >
                  {filteredClients.map((client) => (
                    <button
                      key={client.id}
                      type="button"
                      className={`${styles.clientOption} ${
                        formData.clientId === client.id
                          ? styles.clientOptionSelected
                          : ""
                      }`}
                      onClick={() => handleClientChange(client.id)}
                    >
                      {client.name}
                    </button>
                  ))}

                  {filteredClients.length === 0 && !clientsLoading && (
                    <div className={styles.clientListInfo}>No hay clientes</div>
                  )}
                  {clientsLoading && (
                    <div className={styles.clientListInfo}>
                      Cargando clientes...
                    </div>
                  )}
                  {!clientsLoading &&
                    hasMoreClients &&
                    clientFilter.trim() === "" && (
                      <div className={styles.clientListInfo}>
                        Desliza para cargar más clientes
                      </div>
                    )}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="date">Fecha *</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={(e) =>
                    setFormData({ ...formData, date: e.target.value })
                  }
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="validUntil">Válido hasta *</label>
                <input
                  type="date"
                  id="validUntil"
                  name="validUntil"
                  value={formData.validUntil}
                  onChange={(e) =>
                    setFormData({ ...formData, validUntil: e.target.value })
                  }
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="serviceStartDate">
                  Inicio del servicio
                  {plagues.find((p) => p.id === formData.pestType)
                    ?.isRecurrent && " *"}
                </label>
                <input
                  type="date"
                  id="serviceStartDate"
                  name="serviceStartDate"
                  value={formData.serviceStartDate || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      serviceStartDate: e.target.value,
                    })
                  }
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="serviceEndDate">
                  Fin del servicio
                  {plagues.find((p) => p.id === formData.pestType)
                    ?.isRecurrent && " *"}
                </label>
                <input
                  type="date"
                  id="serviceEndDate"
                  name="serviceEndDate"
                  value={formData.serviceEndDate || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, serviceEndDate: e.target.value })
                  }
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="pestType">Tipo de Plaga *</label>
                <select
                  id="pestType"
                  name="pestType"
                  value={formData.pestType || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      pestType: e.target.value || undefined,
                    })
                  }
                  required
                >
                  <option value="">Seleccionar tipo de plaga...</option>
                  {plagues.map((plague) => (
                    <option key={plague.id} value={plague.id}>
                      {plague.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className={styles.lineItemsSection}>
            <div className={styles.sectionHeader}>
              <h3>Líneas de Servicio</h3>
              <button
                type="button"
                className={styles.addLineButton}
                onClick={addLineItem}
              >
                <Icon name="plus" size={16} />
                Añadir línea
              </button>
            </div>

            <div className={styles.lineItemsTable}>
              <div className={styles.tableHeader}>
                <div className={styles.colService}>Servicio</div>
                <div className={styles.colQuantity}>Cant.</div>
                <div className={styles.colPrice}>Precio Unit.</div>
                <div className={styles.colDiscount}>Desc. %</div>
                <div className={styles.colTotal}>Total</div>
                <div className={styles.colActions}></div>
              </div>

              {formData.lineItems.map((item, index) => (
                <div key={item.id} className={styles.lineItem}>
                  <div className={styles.colService}>
                    <select
                      value={item.serviceId}
                      onChange={(e) =>
                        handleServiceSelection(index, e.target.value)
                      }
                      disabled={serviceCatalog.length === 0}
                      title="Seleccionar servicio del catálogo"
                    >
                      <option value="">
                        {serviceCatalog.length === 0
                          ? "No hay servicios disponibles"
                          : "Seleccionar servicio"}
                      </option>
                      {serviceCatalog.map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.colQuantity}>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) =>
                        updateLineItem(
                          index,
                          "quantity",
                          Number(e.target.value),
                        )
                      }
                      min="1"
                      step="1"
                      required
                      title="La cantidad debe ser al menos 1"
                    />
                  </div>
                  <div className={styles.colPrice}>
                    <span className={styles.readonlyValue}>
                      {formatCurrency(item.unitPrice)}
                    </span>
                  </div>
                  <div className={styles.colDiscount}>
                    <input
                      type="number"
                      value={item.discount}
                      onChange={(e) =>
                        updateLineItem(
                          index,
                          "discount",
                          Number(e.target.value),
                        )
                      }
                      min="0"
                      max="100"
                      step="1"
                      title="Descuento entre 0 y 100%"
                    />
                  </div>
                  <div className={styles.colTotal}>
                    <span className={styles.totalAmount}>
                      {formatCurrency(calculateLineTotal(item))}
                    </span>
                  </div>
                  <div className={styles.colActions}>
                    {formData.lineItems.length > 1 && (
                      <button
                        type="button"
                        className={styles.removeLineButton}
                        onClick={() => removeLineItem(index)}
                        title="Eliminar línea"
                      >
                        <Icon name="xCircle" size={18} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className={styles.totalsSection}>
            <div className={styles.notesColumn}>
              <div className={styles.formGroup}>
                <label htmlFor="notes">Notas / Observaciones</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  rows={4}
                  maxLength={500}
                  placeholder="Información adicional sobre el presupuesto... (máx. 500 caracteres)"
                />
              </div>
            </div>
            <div className={styles.totalsColumn}>
              <div className={styles.totalRow}>
                <span>Subtotal:</span>
                <span>{formatCurrency(formData.subtotal)}</span>
              </div>
              <div className={styles.totalRow}>
                <span>IVA (21%):</span>
                <span>{formatCurrency(formData.tax)}</span>
              </div>
              <div className={styles.totalRowFinal}>
                <span>TOTAL:</span>
                <span>{formatCurrency(formData.total)}</span>
              </div>
            </div>
          </div>

          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
            >
              Cancelar
            </button>
            <button type="submit" className={styles.saveButton}>
              <Icon name="checkCircle" size={18} />
              {quote ? "Guardar Cambios" : "Crear Presupuesto"}
            </button>
          </div>
        </form>
      </div>

      {/* AlertModal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        message={alertModal.message}
        type={alertModal.type}
      />
    </div>
  );
}
